/**
 * Admin WebSocket（/admin/ws/admin）：鉴权后订阅 Redis 转发的 cs_admin_notify 等事件
 */

const handlers = new Map() // event -> Set<fn>

let ws = null
let heartbeatTimer = null
let reconnectTimer = null
let reconnectAttempt = 0
let manualClose = false
let lastToken = ''
let lastPage = 'unknown'

const HEARTBEAT_MS = 30000
const RECONNECT_CAPS = [1000, 2000, 5000, 10000, 30000]

function buildWsUrl() {
  const base = (process.env.VUE_APP_BASE_API || '').trim()
  let u
  try {
    u = base ? new URL(base, typeof window !== 'undefined' ? window.location.origin : 'http://localhost') : new URL(typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
  } catch {
    u = new URL(typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
  }
  const wsProto = u.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${wsProto}//${u.host}/admin/ws/admin`
}

function clearHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

function clearReconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}

function scheduleReconnect() {
  if (manualClose || typeof window === 'undefined') return
  clearReconnect()
  const cap = RECONNECT_CAPS[Math.min(reconnectAttempt, RECONNECT_CAPS.length - 1)]
  reconnectAttempt++
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null
    const t = lastToken || localStorage.getItem('token')
    if (t) connectAdminRealtime(t)
  }, cap)
}

function emitEvent(name, data) {
  const set = handlers.get(name)
  if (!set) return
  set.forEach((fn) => {
    try {
      fn(data)
    } catch (e) {
      console.error('[admin-realtime] handler error', e)
    }
  })
}

function parseInbound(raw) {
  let msg
  try {
    msg = typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch {
    return
  }
  if (!msg || typeof msg !== 'object') return
  if (msg.type === 'event' && msg.event) {
    let data = msg.data
    if (data !== undefined && typeof data !== 'object') {
      try {
        data = typeof data === 'string' ? JSON.parse(data) : data
      } catch {
        data = msg.data
      }
    }
    emitEvent(msg.event, data)
  }
}

function startHeartbeat() {
  clearHeartbeat()
  heartbeatTimer = window.setInterval(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    try {
      ws.send(JSON.stringify({ type: 'heartbeat', page: lastPage }))
    } catch (_) {}
  }, HEARTBEAT_MS)
}

/**
 * @param {string} [token] 不传则从 localStorage 读取
 */
export function connectAdminRealtime(token) {
  if (typeof window === 'undefined' || !window.WebSocket) return
  const t = token || localStorage.getItem('token')
  if (!t) return

  lastToken = t
  manualClose = false

  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return
  }

  clearReconnect()
  const url = buildWsUrl()
  try {
    ws = new WebSocket(url)
  } catch (e) {
    console.error('[admin-realtime] WebSocket construct failed', e)
    scheduleReconnect()
    return
  }

  ws.onopen = () => {
    reconnectAttempt = 0
    try {
      ws.send(JSON.stringify({ type: 'auth', token: t, page: lastPage }))
    } catch (_) {}
    startHeartbeat()
  }

  ws.onmessage = (ev) => {
    parseInbound(ev.data)
  }

  ws.onerror = () => {
    /* onclose will reconnect */
  }

  ws.onclose = () => {
    clearHeartbeat()
    ws = null
    if (!manualClose) scheduleReconnect()
  }
}

export function disconnectAdminRealtime() {
  manualClose = true
  clearHeartbeat()
  clearReconnect()
  reconnectAttempt = 0
  if (ws) {
    try {
      ws.close()
    } catch (_) {}
    ws = null
  }
}

/** 随路由更新，便于心跳携带（可选） */
export function setRealtimePage(page) {
  lastPage = page && String(page).trim() ? String(page).trim() : 'unknown'
}

/**
 * @param {string} event
 * @param {(data: unknown) => void} fn
 */
export function onRealtimeEvent(event, fn) {
  if (!event || typeof fn !== 'function') return () => {}
  let set = handlers.get(event)
  if (!set) {
    set = new Set()
    handlers.set(event, set)
  }
  set.add(fn)
  return () => offRealtimeEvent(event, fn)
}

export function offRealtimeEvent(event, fn) {
  const set = handlers.get(event)
  if (!set) return
  set.delete(fn)
  if (set.size === 0) handlers.delete(event)
}
